import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

export default function LanguageSelector({ mobileVersion = false }) {
  const { i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language || 'fr');

  useEffect(() => {
    setCurrentLang(i18n.language.substring(0, 2) || 'fr');
  }, [i18n.language]);

  const toggleLanguage = () => {
    const newLang = currentLang === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
    setCurrentLang(newLang);
    localStorage.setItem('i18nextLng', newLang);
  };

  return (
    <motion.button
      className="flex items-center justify-center p-1 rounded-md hover:bg-gray-100 transition-colors duration-200"
      onClick={toggleLanguage}
      whileTap={{ scale: 0.95 }}
      title={currentLang === 'fr' ? 'Switch to English' : 'Passer au français'}
      aria-label={currentLang === 'fr' ? 'Switch to English' : 'Passer au français'}
    >
      {mobileVersion ? (
        // Version mobile - drapeau combiné français/anglais comme en desktop mais plus petit
        <svg 
          width="30" 
          height="20" 
          viewBox="0 0 30 20" 
          xmlns="http://www.w3.org/2000/svg"
          className="rounded-sm border border-gray-200"
        >
          {/* Fond blanc et bordure */}
          <rect width="30" height="20" fill="white" stroke="#E0E0E0" strokeWidth="0.5" rx="2" />
          
          {/* Ligne diagonale séparatrice */}
          <line x1="0" y1="0" x2="30" y2="20" stroke="#D1D5DB" strokeWidth="0.5" />
          
          {/* Drapeau français (partie inférieure gauche) */}
          <clipPath id="clipFrenchMobile">
            <path d="M0 0 L0 20 L30 20 L0 0 Z" />
          </clipPath>
          <g clipPath="url(#clipFrenchMobile)">
            <rect x="0" y="0" width="10" height="20" fill="#002395" />
            <rect x="10" y="0" width="10" height="20" fill="white" />
            <rect x="20" y="0" width="10" height="20" fill="#ED2939" />
          </g>
          
          {/* Drapeau UK (partie supérieure droite) */}
          <clipPath id="clipEnglishMobile">
            <path d="M0 0 L30 0 L30 20 L0 0 Z" />
          </clipPath>
          <g clipPath="url(#clipEnglishMobile)">
            <rect x="0" y="0" width="30" height="20" fill="#012169" />
            <path d="M0 0 L30 20 M30 0 L0 20" stroke="white" strokeWidth="2" />
            <path d="M15 0 L15 20 M0 10 L30 10" stroke="white" strokeWidth="4" />
            <path d="M15 0 L15 20 M0 10 L30 10" stroke="#C8102E" strokeWidth="2" />
            <path d="M0 0 L30 20 M30 0 L0 20" stroke="#C8102E" strokeWidth="1" />
          </g>
          
          {/* Indicateur de langue active */}
          {currentLang === 'fr' ? (
            <polygon points="0,0 0,20 30,20 0,0" fill="rgba(0,0,0,0.08)" />
          ) : (
            <polygon points="0,0 30,0 30,20 0,0" fill="rgba(0,0,0,0.08)" />
          )}
        </svg>
      ) : (
        // Version desktop - drapeau combiné français/anglais
        <svg 
          width="32" 
          height="22" 
          viewBox="0 0 30 20" 
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Fond blanc et bordure */}
          <rect width="30" height="20" fill="white" stroke="#E0E0E0" strokeWidth="0.5" rx="2" />
          
          {/* Ligne diagonale séparatrice */}
          <line x1="0" y1="0" x2="30" y2="20" stroke="#D1D5DB" strokeWidth="0.5" />
          
          {/* Drapeau français (partie inférieure gauche) */}
          <clipPath id="clipFrench">
            <path d="M0 0 L0 20 L30 20 L0 0 Z" />
          </clipPath>
          <g clipPath="url(#clipFrench)">
            <rect x="0" y="0" width="10" height="20" fill="#002395" />
            <rect x="10" y="0" width="10" height="20" fill="white" />
            <rect x="20" y="0" width="10" height="20" fill="#ED2939" />
          </g>
          
          {/* Drapeau UK (partie supérieure droite) */}
          <clipPath id="clipEnglish">
            <path d="M0 0 L30 0 L30 20 L0 0 Z" />
          </clipPath>
          <g clipPath="url(#clipEnglish)">
            <rect x="0" y="0" width="30" height="20" fill="#012169" />
            <path d="M0 0 L30 20 M30 0 L0 20" stroke="white" strokeWidth="3" />
            <path d="M15 0 L15 20 M0 10 L30 10" stroke="white" strokeWidth="5" />
            <path d="M15 0 L15 20 M0 10 L30 10" stroke="#C8102E" strokeWidth="3" />
            <path d="M0 0 L30 20 M30 0 L0 20" stroke="#C8102E" strokeWidth="1" />
          </g>
          
          {/* Indicateur de langue active */}
          {currentLang === 'fr' ? (
            <polygon points="0,0 0,20 30,20 0,0" fill="rgba(0,0,0,0.08)" />
          ) : (
            <polygon points="0,0 30,0 30,20 0,0" fill="rgba(0,0,0,0.08)" />
          )}
        </svg>
      )}
      
      <span className="sr-only">
        {currentLang === 'fr' ? 'Switch to English' : 'Passer au français'}
      </span>
    </motion.button>
  );
}
